import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { SOCKET_URL, BASE_URL } from '../config/socket';

const socket = io(SOCKET_URL);

function CustomerDisplay() {
  const [cart, setCart] = useState({
    items: [],
    total: 0,
    paymentMethod: 'cash',
    customerName: null,
    memberName: null,
    cartPromotion: null,
    subtotal: 0,
    manualDiscountType: 'baht',
    manualDiscountValue: 0,
    pointsDiscount: 0
  });
  const [activePromotions, setActivePromotions] = useState([]);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [saleCompleted, setSaleCompleted] = useState(null);
  const [paymentPreview, setPaymentPreview] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [dynamicQrCode, setDynamicQrCode] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchActivePromotions();
    fetchSettings();

    // Check socket connection
    console.log('Socket connecting to:', socket.io.uri);

    socket.on('connect', () => {
      console.log('Socket connected! ID:', socket.id);
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected!');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    // Listen for cart updates
    socket.on('cart-updated', (data) => {
      console.log('Cart updated:', data);
      setCart(data);
      // Don't reset saleCompleted here - let it timeout naturally
    });

    // Listen for promotion updates
    socket.on('promotion-updated', () => {
      fetchActivePromotions();
    });

    // Listen for payment preview (before confirmation)
    socket.on('payment-preview', (data) => {
      console.log('Payment preview:', data);
      setPaymentPreview(data);
    });

    // Listen for clear preview
    socket.on('clear-preview', () => {
      console.log('Clear preview');
      setPaymentPreview(null);
    });

    // Listen for sale completion
    socket.on('sale-complete', (data) => {
      console.log('Sale completed:', data);
      setPaymentPreview(null); // Clear preview
      setSaleCompleted(data);
      setTimeout(() => {
        setSaleCompleted(null);
        setCart({ items: [], total: 0, paymentMethod: 'cash', customerName: null, memberName: null });
      }, 5000);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('cart-updated');
      socket.off('promotion-updated');
      socket.off('payment-preview');
      socket.off('clear-preview');
      socket.off('sale-complete');
    };
  }, []);

  // Auto-rotate promotions
  useEffect(() => {
    if (activePromotions.length > 0) {
      const interval = setInterval(() => {
        setCurrentPromoIndex((prev) => (prev + 1) % activePromotions.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activePromotions]);

  const fetchActivePromotions = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/promotions/active`);
      const promosWithImages = response.data.filter(p => p.image);
      setActivePromotions(promosWithImages);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/settings`);
      console.log('Settings loaded:', response.data);
      setSettings(response.data);
      if (response.data && response.data.payment_qr_code) {
        setQrCode(response.data.payment_qr_code);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  // Generate QR Code when amount changes (auto mode - PromptPay or SCB)
  useEffect(() => {
    const generateQR = async () => {
      if (!settings || settings.qr_mode !== 'auto') {
        setDynamicQrCode(null);
        return;
      }

      if (!cart.total || cart.total <= 0) {
        setDynamicQrCode(null);
        return;
      }

      try {
        // Always use PromptPay for dynamic QR generation
        const endpoint = '/promptpay/generate';

        const response = await axios.post(`${BASE_URL}${endpoint}`, {
          amount: cart.total
        });

        setDynamicQrCode(response.data.qrCode);
      } catch (error) {
        console.error('Error generating QR:', error);
        setDynamicQrCode(null);
      }
    };

    generateQR();
  }, [cart.total, settings]);

  return (
    <div className="h-screen bg-gradient-to-br from-sky-400 via-cyan-500 to-blue-600 flex flex-col overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Header */}
      <div className="bg-white shadow-2xl p-3 sm:p-4 md:p-6 relative z-10 border-b-4 border-sky-500 flex-shrink-0">
        <div className="text-center flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 md:gap-6">
          {settings?.store_logo && (
            <img
              src={`${BASE_URL.replace('/api', '')}${settings.store_logo}`}
              alt="Store Logo"
              className="h-12 sm:h-16 md:h-20 w-auto object-contain drop-shadow-lg animate-bounce"
              style={{animationDuration: '3s'}}
            />
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent break-words leading-tight pt-2 pb-3">
              {settings?.store_name || 'ยินดีต้อนรับสู่ร้านค้าของเรา'}
            </h1>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base md:text-lg italic">" {settings?.slogan || 'เพราะเขาเป็นมากกว่าสัตว์เลี้ยง'} "</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row p-3 sm:p-4 md:p-6 gap-3 sm:gap-4 md:gap-6 overflow-hidden min-h-0">
        {/* Cart Items / QR Code Section */}
        <div className="flex-1 bg-white rounded-lg shadow-2xl p-3 sm:p-4 md:p-6 relative z-10 border-2 border-sky-200 flex flex-col overflow-hidden min-h-0">
          {paymentPreview ? (
            <div className="flex flex-col items-center justify-center h-full animate-fadeIn">
              <div className="text-5xl sm:text-6xl md:text-7xl mb-4 sm:mb-6 animate-pulse">
                ⏳
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-500 to-orange-600 bg-clip-text text-transparent mb-2 sm:mb-4">
                กรุณาตรวจสอบยอดเงิน
              </h3>
              <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-8">รอการยืนยันจากพนักงาน...</p>

              <div className="mt-4 p-8 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl border-2 border-sky-200 space-y-4 w-full max-w-md">
                <div className="text-center">
                  <p className="text-xl text-gray-600">ยอดที่ต้องชำระ</p>
                  <p className="text-5xl font-extrabold text-sky-600 mt-2">
                    ฿{paymentPreview.total?.toFixed(2)}
                  </p>
                </div>

                {paymentPreview.payment_method === 'cash' && (
                  <>
                    <div className="pt-4 border-t-2 border-sky-200 text-center">
                      <p className="text-xl text-gray-600">รับเงินมา</p>
                      <p className="text-4xl font-bold text-blue-600 mt-2">
                        ฿{paymentPreview.paid_amount?.toFixed(2)}
                      </p>
                    </div>

                    <div className="pt-4 border-t-2 border-green-200 text-center bg-green-50 -mx-8 px-8 py-6 rounded-b-xl">
                      <p className="text-2xl text-gray-700 font-semibold">เงินทอน</p>
                      <p className="text-6xl font-extrabold text-green-600 mt-3 drop-shadow-lg animate-pulse">
                        ฿{paymentPreview.change?.toFixed(2)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : saleCompleted ? (
            <div className="flex flex-col items-center justify-center h-full animate-fadeIn">
              <div className="text-9xl mb-6 animate-bounce">
                <span className="inline-block bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent">✓</span>
              </div>
              <h3 className="text-5xl font-bold bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent mb-4 animate-pulse">
                ขายสำเร็จ!
              </h3>
              <p className="text-2xl text-gray-700 font-mono bg-gray-100 px-6 py-3 rounded-lg">
                เลขที่: <span className="font-bold text-sky-600">{saleCompleted.sale_number}</span>
              </p>
              <div className="mt-8 p-8 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl border-2 border-sky-200 space-y-4">
                <div className="text-center">
                  <p className="text-xl text-gray-600">ยอดที่ต้องชำระ</p>
                  <p className="text-5xl font-extrabold text-sky-600 mt-2">
                    ฿{saleCompleted.total?.toFixed(2)}
                  </p>
                </div>

                {saleCompleted.payment_method === 'cash' && (
                  <>
                    <div className="pt-4 border-t-2 border-sky-200 text-center">
                      <p className="text-xl text-gray-600">รับเงินมา</p>
                      <p className="text-4xl font-bold text-blue-600 mt-2">
                        ฿{saleCompleted.paid_amount?.toFixed(2)}
                      </p>
                    </div>

                    <div className="pt-4 border-t-2 border-green-200 text-center bg-green-50 -mx-8 px-8 py-6 rounded-b-xl">
                      <p className="text-2xl text-gray-700 font-semibold">เงินทอน</p>
                      <p className="text-6xl font-extrabold text-green-600 mt-3 drop-shadow-lg">
                        ฿{saleCompleted.change?.toFixed(2)}
                      </p>
                    </div>
                  </>
                )}
              </div>
              <p className="text-gray-600 mt-8 text-2xl flex items-center gap-2">
                <span className="text-3xl">🙏</span>
                ขอบคุณที่ใช้บริการ!
                <span className="text-3xl">🙏</span>
              </p>
            </div>
          ) : cart.paymentMethod === 'transfer' && cart.items.length > 0 && (dynamicQrCode || qrCode) ? (
            <div className="flex flex-col items-center justify-center h-full animate-fadeIn">
              <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-3">
                <span className="text-4xl animate-pulse">📱</span>
                กรุณาโอนเงิน
              </h2>
              <div className="relative">
                <img
                  src={dynamicQrCode || `${BASE_URL.replace('/api', '')}${qrCode}`}
                  alt="QR Code Payment"
                  className="w-96 h-96 object-contain border-4 border-sky-600 rounded-2xl shadow-2xl bg-white p-4 animate-pulse"
                  style={{animationDuration: '2s'}}
                />
                <div className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 font-bold px-4 py-2 rounded-full shadow-lg animate-bounce">
                  สแกนที่นี่!
                </div>
              </div>
              <div className="mt-8 text-center bg-gradient-to-r from-sky-500 to-cyan-600 p-6 rounded-2xl shadow-xl text-white">
                <p className="text-xl font-semibold">ยอดที่ต้องชำระ</p>
                <p className="text-6xl font-extrabold mt-2 drop-shadow-lg">
                  ฿{cart.total?.toFixed(2)}
                </p>
                <p className="text-lg mt-4 opacity-90">สแกน QR Code เพื่อชำระเงิน</p>
                {dynamicQrCode && (
                  <p className="text-sm mt-2 opacity-75">✨ QR โอนตรงตามยอดเงิน</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Welcome Message and Cart Title */}
              <div className="text-center mb-4 flex-shrink-0">
                <div className="text-3xl font-semibold mb-2 text-sky-700">
                  {cart.memberName ? (
                    <span className="flex items-center justify-center gap-2 animate-pulse">
                      <span className="text-4xl">👑</span>
                      สวัสดีครับ คุณ{cart.memberName}
                      <span className="text-4xl">👑</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <span className="text-4xl">😊</span>
                      สวัสดีครับคุณลูกค้า
                      <span className="text-4xl">😊</span>
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent flex items-center justify-center gap-2">
                  <span className="text-2xl">🛒</span>
                  รายการสินค้า
                </h2>
              </div>

              {cart.items.length > 0 ? (
                <div className="space-y-3 overflow-y-auto flex-1 pr-2" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                  {[...cart.items].reverse().map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-4 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl border-2 border-sky-200 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-102 animate-fadeIn"
                      style={{animationDelay: `${index * 0.1}s`}}
                    >
                      <div className="flex-1">
                        <div className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                          <span className="text-xl">📦</span>
                          {item.product_name}
                        </div>
                        <div className="text-lg text-gray-600 mt-1 font-semibold">
                          ฿{item.unit_price?.toFixed(2)} × <span className="bg-sky-600 text-white px-3 py-1 rounded-full text-base">{item.quantity}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-extrabold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent">
                          ฿{((item.unit_price * item.quantity) - (item.discount_amount || 0)).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center flex-1">
                  <div className="text-center text-gray-400 animate-pulse">
                    <div className="text-9xl mb-6">🛒</div>
                    <p className="text-4xl font-bold">ตะกร้าว่างเปล่า</p>
                    <p className="mt-4 text-2xl">สินค้าจะแสดงที่นี่เมื่อเพิ่มลงตะกร้า</p>
                  </div>
                </div>
              )}

              {/* Total Section - Fixed at bottom */}
              {cart.items.length > 0 && (() => {
                // Calculate total discount from all sources
                const productDiscounts = cart.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
                const cartDiscount = cart.cartPromotion?.discount || 0;

                // Calculate manual discount
                const subtotal = cart.subtotal || 0;
                let manualDiscount = 0;
                if (cart.manualDiscountType === 'baht') {
                  manualDiscount = Math.min(cart.manualDiscountValue || 0, subtotal);
                } else if (cart.manualDiscountType === 'percent') {
                  manualDiscount = (subtotal * (cart.manualDiscountValue || 0)) / 100;
                }

                const pointsDiscount = cart.pointsDiscount || 0;
                const totalDiscount = productDiscounts + cartDiscount + manualDiscount + pointsDiscount;

                return (
                  <div className="border-t-4 border-gradient-to-r from-sky-600 to-cyan-600 pt-4 mt-4 bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 p-6 rounded-2xl shadow-lg flex-shrink-0 space-y-3">
                    {/* Show Subtotal if there's a discount */}
                    {totalDiscount > 0 && cart.subtotal > 0 && (
                      <div className="flex justify-between items-center text-2xl">
                        <span className="font-semibold text-gray-700">ยอดรวม:</span>
                        <span className="font-bold text-gray-800">฿{cart.subtotal?.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Total Discount - Show if there's any discount */}
                    {totalDiscount > 0 && (
                      <div className="flex justify-between items-center text-2xl bg-gradient-to-r from-green-50 to-emerald-50 -mx-6 px-6 py-3 border-l-4 border-green-500 shadow-md">
                        <span className="font-semibold text-green-700 flex items-center gap-2">
                          <span className="text-3xl">🎉</span>
                          <span>ส่วนลดรวมทั้งหมด</span>
                        </span>
                        <span className="font-black text-green-600 text-3xl">-฿{totalDiscount.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Net Total */}
                    <div className={`flex justify-between items-center ${totalDiscount > 0 ? 'pt-3 border-t-2 border-orange-300' : ''}`}>
                      <span className="text-4xl font-black text-gray-900 flex items-center gap-3">
                        <span className="text-5xl">💰</span>
                        ยอดสุทธิ:
                      </span>
                      <span className="text-7xl font-black text-red-600 drop-shadow-lg animate-pulse">
                        ฿{cart.total?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Promotions Section */}
        <div className="lg:w-1/3 bg-white rounded-lg shadow-2xl p-6 relative z-10 border-2 border-sky-200 flex flex-col overflow-hidden">
          <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2 flex-shrink-0">
            <span className="text-3xl animate-pulse">🎁</span>
            โปรโมชั่นพิเศษ
          </h2>

          {activePromotions.length > 0 ? (
            <div className="space-y-2 flex-1 flex flex-col min-h-0">
              <div className="relative overflow-hidden rounded-xl shadow-2xl flex-shrink-0 bg-white">
                {/* Sliding Promotion Images */}
                <div className="relative h-96">
                  {activePromotions.map((promo, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-1000 transform ${
                        index === currentPromoIndex
                          ? 'opacity-100 translate-x-0 scale-100'
                          : index < currentPromoIndex
                          ? 'opacity-0 -translate-x-full scale-95'
                          : 'opacity-0 translate-x-full scale-95'
                      }`}
                    >
                      <img
                        src={`${BASE_URL.replace('/api', '')}${promo.image}`}
                        alt={promo.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>

                {/* Sparkle Effect */}
                <div className="absolute top-4 right-4 text-yellow-300 text-4xl animate-spin" style={{animationDuration: '3s'}}>
                  ✨
                </div>
                <div className="absolute bottom-4 left-4 text-yellow-300 text-3xl animate-bounce" style={{animationDuration: '2s'}}>
                  ⭐
                </div>
              </div>

              {/* Promotion Dots */}
              <div className="flex justify-center gap-3 py-2 flex-shrink-0">
                {activePromotions.map((_, index) => (
                  <div
                    key={index}
                    className={`h-3 rounded-full transition-all duration-500 cursor-pointer hover:scale-110 ${
                      index === currentPromoIndex
                        ? 'bg-gradient-to-r from-sky-500 to-cyan-600 w-12 shadow-lg'
                        : 'bg-gray-300 w-3 hover:bg-gray-400'
                    }`}
                    onClick={() => setCurrentPromoIndex(index)}
                  />
                ))}
              </div>

              {/* Current Active Promotion Details */}
              <div className="mt-2 flex-1 min-h-0">
                {activePromotions[currentPromoIndex] && (() => {
                  const promo = activePromotions[currentPromoIndex];
                  const nameLength = promo.name?.length || 0;
                  const descLength = promo.description?.length || 0;
                  const totalLength = nameLength + descLength;

                  // Auto scale font sizes based on content length
                  const nameFontSize = totalLength > 150 ? 'text-base' : totalLength > 100 ? 'text-lg' : totalLength > 80 ? 'text-xl' : 'text-2xl';
                  const descFontSize = totalLength > 150 ? 'text-xs' : totalLength > 100 ? 'text-sm' : totalLength > 80 ? 'text-sm' : 'text-base';
                  const badgeFontSize = totalLength > 150 ? 'text-base' : totalLength > 100 ? 'text-lg' : totalLength > 80 ? 'text-xl' : 'text-2xl';
                  const emojiFontSize = totalLength > 150 ? 'text-lg' : totalLength > 100 ? 'text-xl' : totalLength > 80 ? 'text-2xl' : 'text-3xl';
                  const emojiNameSize = totalLength > 150 ? 'text-xl' : totalLength > 100 ? 'text-2xl' : 'text-3xl';

                  return (
                    <div className="relative rounded-2xl shadow-2xl overflow-hidden animate-fadeIn border-4 border-yellow-400 bg-white h-full">
                      {/* Multiple Sparkle Effects */}
                      <div className="absolute top-2 right-2 text-yellow-400 text-3xl animate-spin" style={{animationDuration: '3s'}}>✨</div>
                      <div className="absolute bottom-2 left-2 text-yellow-400 text-2xl animate-bounce" style={{animationDuration: '2s'}}>⭐</div>

                      <div className="relative p-3 h-full flex flex-col justify-center">
                        {/* Promotion Name */}
                        <div className="text-center mb-2 px-2">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <span className={emojiNameSize}>🎯</span>
                            <span className={emojiNameSize}>🎯</span>
                          </div>
                          <h3 className={`font-black ${nameFontSize} text-red-600 leading-tight break-words animate-pulse overflow-wrap-anywhere`}>
                            {promo.name}
                          </h3>
                        </div>

                        {/* Promotion Description */}
                        {promo.description && (
                          <div className="mb-2 px-2">
                            <p className={`text-black font-bold ${descFontSize} leading-snug text-center break-words overflow-wrap-anywhere bg-yellow-50 py-1 px-2 rounded-xl border-2 border-yellow-300`}>
                              {promo.description}
                            </p>
                          </div>
                        )}

                        {/* Promotion Discount Badge */}
                        <div className="flex items-center justify-center gap-2 flex-wrap px-2">
                          {promo.type === 'percentage' && (
                            <div className="w-full">
                              <div className={`px-3 py-2 rounded-xl font-black shadow-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white ${badgeFontSize} border-4 border-white text-center break-words`}>
                                <div className="flex items-center justify-center gap-2 flex-wrap">
                                  <span className={emojiFontSize}>🏷️</span>
                                  <span className="break-words">ลด {promo.discount_percentage}%</span>
                                </div>
                              </div>
                            </div>
                          )}
                          {promo.type === 'fixed_amount' && (
                            <div className="w-full">
                              <div className={`px-3 py-2 rounded-xl font-black shadow-xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white ${badgeFontSize} border-4 border-white text-center break-words`}>
                                <div className="flex items-center justify-center gap-2 flex-wrap">
                                  <span className={emojiFontSize}>💵</span>
                                  <span className="break-words">ลด ฿{parseFloat(promo.discount_value).toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          {promo.type === 'buy_x_get_y' && (
                            <div className="w-full">
                              <div className={`px-3 py-2 rounded-xl font-black shadow-xl bg-gradient-to-r from-orange-500 to-red-600 text-white ${badgeFontSize} border-4 border-white text-center break-words`}>
                                <div className="flex items-center justify-center gap-2 flex-wrap">
                                  <span className={emojiFontSize}>🎁</span>
                                  <span className="break-words">ซื้อ {promo.buy_quantity} แถม {promo.get_quantity}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Member Only Badge */}
                          {promo.members_only && (
                            <div className="w-full mt-1">
                              <div className={`px-3 py-1 rounded-xl font-black shadow-xl bg-gradient-to-r from-purple-500 to-pink-600 text-white ${totalLength > 150 ? 'text-base' : totalLength > 100 ? 'text-lg' : 'text-xl'} border-4 border-white text-center animate-bounce break-words`}>
                                <div className="flex items-center justify-center gap-2 flex-wrap">
                                  <span className={totalLength > 150 ? 'text-2xl' : 'text-3xl'}>👑</span>
                                  <span className="break-words">สมาชิกเท่านั้น</span>
                                  <span className={totalLength > 150 ? 'text-2xl' : 'text-3xl'}>👑</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400 animate-pulse">
                <div className="text-9xl mb-6">🎁</div>
                <p className="text-3xl font-bold">ไม่มีโปรโมชั่น</p>
                <p className="text-xl mt-4">โปรดติดตามโปรโมชั่นในภายหลัง</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-sky-600 via-cyan-600 to-blue-600 shadow-2xl p-4 relative z-10">
        <div className="flex items-center justify-center gap-3">
          <span className="text-3xl animate-bounce" style={{animationDuration: '2s'}}>🙏</span>
          <p className="text-center text-white text-xl font-semibold drop-shadow-lg">
            {settings?.footer_text || 'ขอบคุณที่เลือกใช้บริการ - อายุ วัณโณ สุขัง พะลัง'}
          </p>
          <span className="text-3xl animate-bounce" style={{animationDuration: '2s', animationDelay: '0.5s'}}>🙏</span>
        </div>
      </div>
    </div>
  );
}

export default CustomerDisplay;
